import { useCallback, useMemo } from 'react'

import { t } from 'i18next'
import { useFormContext } from 'react-hook-form'

import { RepresentanteLegalUseCase } from '@/features/proposta/entities/Comprador/use-case/representante-legal-use-case'
import type { BaseRepresentanteLegalHookProps } from '@/features/proposta/interfaces/compradores.types'
import type { CompradorPessoaJuridicaSchemaInput } from '@/features/proposta/schemas/compradores/comprador-pj.schema'
import { showToast } from '@/shared/lib/sonner'

export function useDadosRepresentanteLegal({
  onNext,
  compradores,
  refetch,
  nacionalidadePrincipalId,
}: BaseRepresentanteLegalHookProps) {
  const { trigger, setValue, watch, setError } = useFormContext<CompradorPessoaJuridicaSchemaInput>()
  const compradorAtual = watch('responsavel')
  const cpfRepresentante = compradorAtual!.dados.cpf
  const buscouCpf = compradorAtual?.dados.buscouCpf
  const cpfRaw = cpfRepresentante.replace(/\D/g, '')

  const useCase = useMemo(
    () =>
      new RepresentanteLegalUseCase(
        compradores,
        compradorAtual,
        compradorAtual?.emailsJaVinculados || {},
        nacionalidadePrincipalId ?? '',
      ),
    [compradores, compradorAtual, nacionalidadePrincipalId],
  )

  const validateOnNext = useCallback(async () => {
    const { ok, error } = useCase.validarAvanco(cpfRaw, !!buscouCpf)
    if (!ok) {
      if (error === 'MISSING_CPF') return trigger('responsavel.dados.cpf')
      if (error === 'NEED_SEARCH')
        return showToast({
          variant: 'error',
          message: t('proposta:cadastro_compradores.steps.dados_comprador.erro.busca_obrigatoria'),
        })
    }
    if (await trigger('responsavel.dados')) onNext()
  }, [cpfRaw, buscouCpf, trigger, onNext, useCase])

  const handleBuscarCliente = useCallback(async () => {
    const validacaoCpf = useCase.validarCpfOutrosRepresentantes(cpfRaw)

    if (!validacaoCpf.ok) {
      setError('responsavel.dados.cpf', {
        message: t('proposta:cadastro_compradores.steps.dados_comprador.erro.cpf_ja_vinculado'),
      })
      return
    }

    const result = await refetch()
    const fetchResult = useCase.interpretarResultadoBuscaManual(result)
    switch (fetchResult.type) {
      case 'SUCCESS':
        showToast({
          variant: 'success',
          message: t('proposta:cadastro_compradores.steps.dados_comprador.busca.sucesso'),
        })
        setValue('responsavel', fetchResult.patch)
        break
      case 'NOT_FOUND':
        showToast({
          variant: 'info',
          message: t('proposta:cadastro_compradores.steps.dados_comprador.busca.nao_encontrado'),
        })
        setValue('responsavel.dados.buscouCpf', true)
        break
      case 'ERROR':
        showToast({
          variant: 'error',
          message: t('proposta:cadastro_compradores.steps.dados_comprador.busca.erro'),
        })
        break
    }
  }, [cpfRaw, refetch, setError, setValue, useCase])

  const handleLimpar = useCallback(() => {
    setValue('responsavel', useCase.limparFormulario(), { shouldValidate: false })
  }, [setValue, useCase])

  return {
    handlers: {
      validateOnNext,
      handleBuscarCliente,
      handleLimpar,
    },
  }
}
